import type {CustomDealProduct} from '../types';

import {ArrowLeftOutlined, ShoppingCartOutlined} from '@ant-design/icons';
import {Button, Checkbox, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import Empty from 'common/empty/Empty';
import {useEffect, useMemo, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  deleteProductFromDeal,
  getDealCategories,
  getDealProductDiscountIds,
  getDealProducts,
  resetDealProducts
} from 'store/slices/custom-deal';
import {StringParam, useQueryParams, withDefault} from 'use-query-params';

import {CUSTOM_DEAL_PAGE_SIZE, CUSTOM_DEAL_ROUTES} from '../constants';
import {getCustomDealVendorId} from '../utils';
import ApplyDiscountFilters from './components/ApplyDiscountFilters/ApplyDiscountFilters';
import ApplyDiscountListItem from './components/ApplyDiscountListItem/ApplyDiscountListItem';
import ApplyDiscountListItemSkeleton from './components/ApplyDiscountListItemSkeleton/ApplyDiscountListItemSkeleton';
import SetBulkDiscountModal from './components/SetBulkDiscountModal/SetBulkDiscountModal';
import SetOneDiscountModal from './components/SetOneDiscountModal/SetOneDiscountModal';

const ApplyDiscount: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string | number>>(new Set());
  const [selectedProductToEdit, setSelectedProductToEdit] = useState<CustomDealProduct | null>(null);
  const [pagination, setPagination] = useState({page: 1, pageSize: CUSTOM_DEAL_PAGE_SIZE});
  const [query, setQuery] = useQueryParams({
    search: withDefault(StringParam, null),
    stock_status: withDefault(StringParam, null),
    sort: withDefault(StringParam, null),
    badge: withDefault(StringParam, null),
    categories: withDefault(StringParam, null)
  });
  const [showBulkDiscountModal, setShowBulkDiscountModal] = useState(false);
  const [showProductDiscountModal, setShowProductDiscountModal] = useState(false);
  const vendorId = getCustomDealVendorId();
  const {dealProducts, dealProductsLoading, dealCategories, idToDiscount} = useAppSelector(
    (state) => state.customDeal
  );
  const {dealId} = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('isEditing');
  const hasProductWithNoDiscount = useMemo(
    () => Object.values(idToDiscount || {}).some((value) => value === 0),
    [idToDiscount]
  );
  const totalDealProductIds = useMemo(
    () => Object.keys(idToDiscount ?? {}).map((id) => Number(id)),
    [idToDiscount]
  );
  const allProductsSelected = useMemo(
    () =>
      totalDealProductIds.length > 0 &&
      totalDealProductIds.every((id) => selectedProductIds.has(id)),
    [totalDealProductIds, selectedProductIds]
  );
  const someProductsSelected = selectedProductIds.size > 0 && !allProductsSelected;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!dealId) {
      return;
    }
    dispatch(resetDealProducts());
    void dispatch(getDealProductDiscountIds({dealId, vendorId}));
    void dispatch(getDealCategories({dealId, vendorId}));
  }, [dealId, vendorId, dispatch]);

  const getItems = ({pageSize, page}: {pageSize?: number; page?: number}) => {
    const categoriesArray = query.categories
      ? query.categories.split(',').filter((category) => category.trim() !== '')
      : [];
    void dispatch(
      getDealProducts({
        deal_id: dealId,
        vendor_id: vendorId,
        page_size: pageSize ? pageSize : pagination.pageSize,
        page: page ? page : pagination.page,
        search: query.search,
        stock_status: query.stock_status,
        badge: query.badge,
        sort: query.sort,
        parent_category_ids: categoriesArray
      })
    );
  };

  const loadMore = () => {
    if (!dealId) {
      return;
    }
    getItems({page: pagination.page + 1});
    setPagination({pageSize: pagination.pageSize, page: pagination.page + 1});
  };

  useEffect(() => {
    dispatch(resetDealProducts());
    getItems({page: 1});
    setPagination({page: 1, pageSize: pagination.pageSize});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleDeleteProduct = async (productId: string | number) => {
    if (!dealId) {
      return;
    }

    try {
      const response = (await dispatch(
        deleteProductFromDeal({
          dealId,
          vendorId,
          productId
        })
      ).unwrap()) as {data?: {errors?: {message?: string}}};

      if (response.data?.errors?.message) {
        toast(response.data.errors.message || 'خطا در حذف محصول');
        return;
      }

      toast('محصول با موفقیت حذف شد');
      dispatch(resetDealProducts());
      void dispatch(getDealProductDiscountIds({dealId, vendorId}));
      setSelectedProductIds((current) => {
        const next = new Set(current);
        next.delete(productId);
        return next;
      });
      void dispatch(getDealCategories({dealId, vendorId}));
      getItems({pageSize: pagination.pageSize, page: 1});
      setPagination({page: 1, pageSize: pagination.pageSize});
    } catch {
      toast('خطایی در برقراری ارتباط با سرور رخ داد');
    }
  };

  return (
    <PageLayout
      id="selectProductsListWrapper"
      footer={
        <div className="flex w-full flex-col justify-between gap-2">
          <div className="flex items-center gap-1">
            <ShoppingCartOutlined className="text-body-lg" aria-hidden />
            <span className="text-body-sm">
              {selectedProductIds.size > 0
                ? `${selectedProductIds.size} کالا انتخاب شده`
                : 'کالایی انتخاب نشده '}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              disabled={selectedProductIds.size < 2}
              fullWidth
              size="lg"
              onClick={() => setShowBulkDiscountModal(true)}
            >
              ایجاد تخفیف گروهی
            </Button>
            {!isEditing ? (
              <Button
                size="lg"
                variant="outline"
                fullWidth
                endIcon={<ArrowLeftOutlined aria-hidden />}
                disabled={hasProductWithNoDiscount}
                onClick={() => {
                  if (isEditing === 'true') {
                    navigate(CUSTOM_DEAL_ROUTES.detail.replace(':dealId', String(dealId)));
                    return;
                  }
                  navigate(CUSTOM_DEAL_ROUTES.selectDate.replace(':dealId', String(dealId)));
                }}
              >
                ادامه
              </Button>
            ) : null}
          </div>
        </div>
      }
      footerClassName="min-h-27.5 rounded-t-2xl px-4 py-2 shadow-[0_-4px_12px_rgb(0_0_0_/_0.1)]"
    >
      <ApplyDiscountFilters
        filters={query}
        onChangeFilter={(name, value) => {
          setQuery((current) => ({...current, [name]: value}), 'replace');
        }}
        dealCategories={dealCategories}
      />
      <div className="flex items-center gap-4 border-b border-gray-02 px-4 py-4 text-body-sm-emphasis">
        <Checkbox
          onChange={(event) => {
            if (event.target.checked) {
              const allProductIds = new Set<string | number>();
              for (const productId in idToDiscount) {
                allProductIds.add(Number(productId));
              }
              setSelectedProductIds(allProductIds);
              return;
            }
            setSelectedProductIds(new Set());
          }}
          checked={allProductsSelected}
          indeterminate={someProductsSelected}
        />
        <span>انتخاب همه </span>
      </div>
      {!dealProductsLoading && dealProducts.current === undefined ? (
        <div className="flex min-h-50 flex-1 items-center justify-center">
          <Empty>
            <p>هیچ کالایی برای نمایش وجود ندارد</p>
          </Empty>
        </div>
      ) : null}
      <div className="flex flex-col">
        <InfiniteScroll
          scrollableTarget="selectProductsListWrapper"
          scrollThreshold={0.8}
          pullDownToRefreshContent={<></>}
          pullDownToRefreshThreshold={50}
          next={loadMore}
          hasMore={dealProducts?.current?.length < dealProducts?.total_items}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
          dataLength={dealProducts?.current?.length || 0}
        >
          {dealProductsLoading && (!dealProducts?.current || dealProducts.current.length === 0) ? (
            Array.from({length: 5}).map((_, index) => (
              <ApplyDiscountListItemSkeleton key={`skeleton-${index}`} />
            ))
          ) : (
            dealProducts?.current?.map((item: CustomDealProduct) => (
              <ApplyDiscountListItem
                key={item.id}
                item={item}
                selected={selectedProductIds.has(item.id)}
                handleProductSelection={(product) => {
                  setSelectedProductIds((current) => {
                    const next = new Set(current);
                    if (product.selected) {
                      next.add(product.id);
                    } else {
                      next.delete(product.id);
                    }
                    return next;
                  });
                }}
                handleDeleteProduct={handleDeleteProduct}
                onEditProduct={(product) => {
                  setSelectedProductToEdit(product);
                  setShowProductDiscountModal(true);
                }}
                idToDiscount={idToDiscount}
              />
            ))
          )}
        </InfiniteScroll>
      </div>
      <SetBulkDiscountModal
        show={showBulkDiscountModal}
        onClose={() => setShowBulkDiscountModal(false)}
        selectedProductIds={selectedProductIds}
        currentDealId={dealId}
        onSuccess={() => {
          if (dealId) {
            void dispatch(getDealProductDiscountIds({dealId, vendorId}));
          }
        }}
      />
      <SetOneDiscountModal
        show={showProductDiscountModal}
        onClose={() => setShowProductDiscountModal(false)}
        selectedProductToEdit={selectedProductToEdit}
        currentDealId={dealId}
        onSuccess={() => {
          if (dealId) {
            void dispatch(getDealProductDiscountIds({dealId, vendorId}));
          }
        }}
      />
    </PageLayout>
  );
};

export default ApplyDiscount;
